import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { AccessSiem } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime, timeAgo } from '../common'

/**
 * «Inoltro a un SIEM» (CENSO ACCESS settings): the register — and, if asked,
 * the detections — forwarded in order and without holes to the tenant's
 * SIEM, from the Control Plane or from one of its probes, as syslog (UDP,
 * TCP, TLS) or HTTPS (Splunk HEC, webhook), in CEF or JSON. The cursor moves
 * only once a batch is sent; what is masked in the register stays masked.
 */
export function SezioneSiem({ scrive, sonde }: { scrive: boolean; sonde: { id: string; name: string }[] }) {
  const [s, setS] = useState<AccessSiem | null>(null)
  const [token, setToken] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [nota, setNota] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { api.accessSiem().then(setS).catch(setErrore) }, [])

  async function salva() {
    if (!s) return
    setBusy(true)
    setErrore(null)
    setNota(null)
    try {
      setS(await api.accessSiemSave({
        enabled: s.enabled, via: s.via, probe_id: s.probe_id ?? '', transport: s.transport, host: s.host,
        port: s.port, format: s.format, filter: s.filter, detections: s.detections, tls_verify: s.tls_verify,
        url: s.url, kind: s.kind, header: s.header, ...(token ? { token } : {}),
      }))
      setToken('')
      setNota(t('Salvato: la modifica è nel registro.'))
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function prova() {
    setErrore(null)
    setNota(null)
    try {
      const r = await api.accessSiemTest()
      setNota(r.ok ? (r.queued ? t('Evento di prova affidato alla sonda: parte al prossimo contatto.') : t('Evento di prova inviato.')) : (r.error ?? t('Non inviato.')))
    } catch (e) {
      setErrore(e)
    }
  }

  if (!s) return errore ? <ErrorBanner error={errore} /> : <Spinner />
  const https = s.transport === 'https'
  return (
    <Card title={t('Inoltro a un SIEM')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Il registro (e, se vuoi, i rilevamenti) va al SIEM del tenant, in ordine e senza buchi: un lotto si considera consegnato solo dopo l’invio, quindi un SIEM che non risponde non perde nulla. Parte dal Control Plane se il SIEM è raggiungibile da Internet, da una sonda se sta nella rete del cliente. Ciò che il registro non nomina (utenti non privilegiati spenti) non viene nominato nemmeno al SIEM.')}
      </p>
      <fieldset disabled={!scrive || busy} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="form-grid">
          <label className="checkbox span-6"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />{' '}{t('Inoltra gli eventi al SIEM')}</label>
          <label className="field span-2"><span className="field__label">{t('Da dove')}</span>
            <select value={s.via} onChange={(e) => setS({ ...s, via: e.target.value as AccessSiem['via'] })}>
              <option value="cp">{t('dal Control Plane')}</option>
              <option value="probe">{t('da una sonda')}</option>
            </select></label>
          {s.via === 'probe' && (
            <label className="field span-4"><span className="field__label">{t('Sonda')}</span>
              <select value={s.probe_id ?? ''} onChange={(e) => setS({ ...s, probe_id: e.target.value || null })}>
                <option value="">{t('scegli la sonda')}</option>
                {sonde.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select></label>
          )}
          <label className="field span-2"><span className="field__label">{t('Protocollo')}</span>
            <select value={s.transport} onChange={(e) => setS({ ...s, transport: e.target.value as AccessSiem['transport'] })}>
              <option value="syslog_tcp">{t('syslog su TCP')}</option>
              <option value="syslog_tls">{t('syslog su TLS')}</option>
              <option value="syslog_udp">{t('syslog su UDP')}</option>
              <option value="https">HTTPS</option>
            </select></label>
          {https ? (
            <>
              <label className="field span-4"><span className="field__label">{t('Indirizzo del collettore')}</span>
                <input type="url" value={s.url} onChange={(e) => setS({ ...s, url: e.target.value })} placeholder="https://splunk.ente.local:8088/services/collector" /></label>
              <label className="field span-2"><span className="field__label">{t('Tipo')}</span>
                <select value={s.kind} onChange={(e) => setS({ ...s, kind: e.target.value as AccessSiem['kind'] })}>
                  <option value="splunk_hec">{t('Splunk HEC')}</option>
                  <option value="webhook">{t('webhook JSON')}</option>
                </select></label>
              {s.kind === 'webhook' && (
                <label className="field span-2"><span className="field__label">{t('Intestazione del token')}</span>
                  <input type="text" value={s.header} onChange={(e) => setS({ ...s, header: e.target.value })} /></label>
              )}
              <label className="field span-2"><span className="field__label">{s.token_set ? t('Token (impostato: scrivi per cambiarlo)') : t('Token')}</span>
                <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="new-password" /></label>
            </>
          ) : (
            <>
              <label className="field span-3"><span className="field__label">{t('Indirizzo del SIEM')}</span>
                <input type="text" value={s.host} onChange={(e) => setS({ ...s, host: e.target.value })} placeholder={t('siem.esempio.local')} /></label>
              <label className="field span-1"><span className="field__label">{t('Porta')}</span>
                <input type="number" min={1} max={65535} value={s.port} onChange={(e) => setS({ ...s, port: Number(e.target.value) })} /></label>
            </>
          )}
          <label className="field span-2"><span className="field__label">{t('Formato')}</span>
            <select value={s.format} onChange={(e) => setS({ ...s, format: e.target.value as AccessSiem['format'] })}>
              <option value="cef">CEF</option>
              <option value="json">JSON</option>
            </select></label>
          <label className="field span-2"><span className="field__label">{t('Cosa inoltrare')}</span>
            <select value={s.filter} onChange={(e) => setS({ ...s, filter: e.target.value as AccessSiem['filter'] })}>
              <option value="all">{t('tutti gli eventi')}</option>
              <option value="privileged">{t('solo i privilegiati')}</option>
              <option value="privileged_failures">{t('privilegiati e falliti')}</option>
            </select></label>
          <label className="checkbox span-2"><input type="checkbox" checked={s.detections} onChange={(e) => setS({ ...s, detections: e.target.checked })} />{' '}{t('anche i rilevamenti')}</label>
          {(https || s.transport === 'syslog_tls') && (
            <label className="checkbox span-2"><input type="checkbox" checked={s.tls_verify} onChange={(e) => setS({ ...s, tls_verify: e.target.checked })} />{' '}{t('verifica il certificato')}</label>
          )}
        </div>
      </fieldset>
      {s.enabled && (
        <p className="field__hint">
          {t('Inviati {n} messaggi', { n: s.sent.toLocaleString() })}
          {s.last_at && <> · {t('ultimo invio {quando}', { quando: timeAgo(s.last_at) })}</>}
          {s.behind > 0 && <> · <span className="badge badge--warn">{t('{n} eventi da inviare', { n: s.behind })}</span></>}
          {s.last_error && <> · <span className="eagle-motivo" title={formatDateTime(s.last_at)}>{s.last_error}</span></>}
        </p>
      )}
      <ErrorBanner error={errore} />
      {nota && <p className="muted">{nota}</p>}
      {scrive && (
        <div className="form-dialog__azioni">
          <button type="button" onClick={() => void salva()} disabled={busy}>{t('Salva')}</button>
          <button type="button" className="secondary" onClick={() => void prova()} disabled={!s.enabled}>{t('Invia un evento di prova')}</button>
        </div>
      )}
    </Card>
  )
}
